import { urlRoots } from '../config';

const convertToLatin = (str) => {
	if (str === undefined) return '';
	if (typeof str !== 'string') str = str.toString();

	const lookup = [
		/&/g, 'and',
		/[\300-\306]/g, 'A',
		/[\340-\346]/g, 'a',
		/\307/g, 'C',
		/\347/g, 'c',
		/[\310-\313]/g, 'E',
		/[\350-\353]/g, 'e',
		/[\314-\317]/g, 'I',
		/[\354-\357]/g, 'i',
		/\321/g, 'N',
		/\361/g, 'n',
		/[\322-\330]/g, 'O',
		/[\362-\370]/g, 'o',
		/[\331-\334]/g, 'U',
		/[\371-\374]/g, 'u',
	];

	for (let i = 0; i < lookup.length; i += 2) {
		str = str.replace(lookup[i], lookup[i + 1]);
	}

	return str;
};

let decoder;

const decode = (str = '') => {
	if (typeof str !== 'string') str = str.toString();
	if (typeof window === 'undefined') {
		const he = require('he');
		return he.decode(str);
	}

	decoder = decoder || document.createElement('div');
	const html = escape(str).replace(/%26/g, '&').replace(/%23/g, '#').replace(/%3B/g, ';');
	decoder.innerHTML = html;

	return unescape(decoder.textContent);
};

const urlify = (name) => {
	// Replace diacritics
	name = convertToLatin(name);

	// Replace whitespace
	name = name.trim().replace(/\s+/g, '-');

	// Remove anything else
	name = name.replace(/[^a-z0-9_-]+/gi, '').toLowerCase();

	return name;
};

const cleanEmail = (email) => {
	// Replace diacritics
	email = convertToLatin(email);

	// Replace whitespace
	email = email.replace(/\s+/g, '');

	// Remove anything else
	email = email.toLowerCase().replace(/[^a-z0-9_\-.@]+/g, '');

	return email;
};

const upperCaseFirst = (string) => string.charAt(0).toUpperCase() + string.slice(1);

const lowerCaseFirst = (string) => string.charAt(0).toLowerCase() + string.slice(1);

const deCamel = (string) => {
	const newString = String(string).replace(/[a-z0-9](?=[A-Z])/g, (v) => `${v} `);
	return upperCaseFirst(newString);
};

// This Is Title Case
const titleCase = (string) => {
	const newString = String(string).toLowerCase().replace(/(\s|-|_)\S/g, (v) => v.toUpperCase());
	return upperCaseFirst(newString);
};

// This is sentence case
const sentenceCase = (string) => {
	const newString = String(string).toLowerCase().replace(/(\s|-|_)/g, ' ');
	return upperCaseFirst(newString);
};

// ThisIsPascalCase
const pascalCase = (string) => titleCase(string).replace(/(\s|-|_)/g, '');

// thisIsCamelCase
const camelCase = (string) => lowerCaseFirst(pascalCase(string));

// ~ equal to that of PHP's number_format
const formatNumber = (int, decimals = 2, decimal = '.', thousands = ' ') => {
	// Forces a decimal where the current decimal is
	int = parseFloat(int).toFixed(decimals).toString().replace(/\.(\d*)$/, `${decimal}$1`);
	// Get last Index of decimal and work backwards to get thousands seperator
	let fromIndex = int.lastIndexOf(decimal) !== -1 ? int.lastIndexOf(decimal) : int.length;
	fromIndex = fromIndex - 3 > 0 ? fromIndex - 3 : 0;
	const numberSplit = int.split('');
	// Insert thousands.
	for (let i = fromIndex; i > 0; i -= 3) {
		numberSplit.splice(i, 0, thousands);
	}
	return numberSplit.join('');
};

const formatPrice = (setup, amount) => {
	const currencyData = setup.currencies[setup.currency];
	if (!currencyData) return amount;
	return `${currencyData.prefix}${formatNumber(amount, currencyData.decimalDigits, currencyData.decimalPoint)}${currencyData.suffix}`;
};

const fieldValidation = (value, type) => {
	if (type === 'address2' || type === 'company') return false;
	if (value === undefined) return '';

	if (!/\S/.test(value)) return 'Empty';

	if (type.indexOf('email') !== -1 && (value.length < 6 || value.indexOf('@') < 1 || value.lastIndexOf('.') < 2 || value.lastIndexOf('.') === value.length - 1)) {
		return 'EmailIncorrect';
	}

	if (type.indexOf('password') !== -1 && value.trim().length < 8) {
		return 'PasswordIncorrect';
	}

	if (type.indexOf('phone') !== -1 && value.length < 6) {
		return 'PhoneIncorrect';
	}

	if (type.indexOf('state') !== -1 && !value) {
		return 'StateIncorrect';
	}

	if (type.indexOf('terms') !== -1 && !value) {
		return 'TermsIncorrect';
	}

	return false;
};

const cleanInternalLinks = (link) => {
	if (!link) return '';
	if (typeof link !== 'string') link = link.toString();

	// Link to an asset.
	if (/\..{1,4}$/.test(link.replace(/\?.*$/, ''))) return link;

	urlRoots.forEach((base) => {
		if (link.indexOf(base) !== -1 && !/^mailto/.test(link)) {
			const regexp = new RegExp(`^(https?:)?//[^/]*${base}[^/]*/`);
			link = link.replace(regexp, '/');
		}
	});

	// Ensure link isn't anchor/external
	if (!/^#/.test(link) && !/^(https?|mailto|tel)/.test(link)) {
		// Remove possible language looking part.
		if (/^\/\w{2}\//.test(link)) link = link.slice(3);
		if (!/^\//.test(link)) link = `/${link}`;
		if (/\/$/.test(link) && link.length > 1) link = link.slice(0, -1);
	}

	return link;
};

/*
[
	'#E53',
	'#8000d7',
	'#8000d7CD',
	'rgb(1,0,124)',
	'rgba(123,45,9,0.7)',
].map(getRGB);
*/

const getRGB = (colorCode) => {
	if (colorCode.indexOf('#') === 0) {
		if (colorCode.length === 4) return colorCode.match(/\w/g).map((x) => parseInt(`${x}${x}`, 16));
		return colorCode.match(/\w\w/g).map((x, i) => (i === 3 ? Math.round((parseInt(x, 16) / 255) * 100) / 100 : parseInt(x, 16)));
	}
	if (colorCode.indexOf('rgb') === 0) return colorCode.match(/[\d.]+/g).map((x, i) => (i === 3 ? parseFloat(x) : parseInt(x, 10)));
	// No match return black.
	return [0, 0, 0];
};

const hex2rgba = (hex, alpha = 1) => {
	const [r, g, b] = getRGB(hex);
	return `rgba(${r},${g},${b},${alpha})`;
};

const luminance = (hex) => {
	const [r, g, b] = getRGB(hex);
	return (r * 0.299) + (g * 0.587) + (b * 0.114);
};

const blackOrWhite = (hex) => (luminance(hex) > 150 ? '#000' : '#FFF');

const splitNewLine = (str) => (str ? str.split(/\r?\n/) : []);

const generateEventId = () => `${`${Date.now()}`.slice(-8)}_${Math.random() * 1e10}`.replace('.', '');

export {
	decode,
	convertToLatin,
	urlify,
	cleanEmail,
	upperCaseFirst,
	lowerCaseFirst,
	deCamel,
	titleCase,
	sentenceCase,
	pascalCase,
	camelCase,
	formatNumber,
	formatPrice,
	fieldValidation,
	hex2rgba,
	blackOrWhite,
	cleanInternalLinks,
	splitNewLine,
	generateEventId,
};
